// src/Register.js
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [institution, setInstitution] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  const createUserDocumentIfNotExists = async (user, additionalData = {}) => {
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      await setDoc(docRef, {
        firstName: additionalData.firstName || '',
        lastName: additionalData.lastName || '',
        email: user.email || '',
        role: additionalData.role || '',
        institution: additionalData.institution || '',
        registrationTimestamp: new Date(),
        trialCredits: 30,
        emailVerified: false,
        termsAccepted: true,
        termsAcceptedDate: new Date(),
      });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Check if terms are accepted
    if (!termsAccepted) {
      alert('You must accept the Terms and Conditions to register.');
      return;
    }
    
    // Check if passwords match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    // Check password strength
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    setPasswordError('');
    
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Send verification email with redirect URL
      await sendEmailVerification(user, {
        url: window.location.origin + '/login?verified=true'
      });
      setVerificationSent(true);

      // Save additional information in Firestore
      await createUserDocumentIfNotExists(user, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role: role.trim(),
        institution:
          role === 'student' || role === 'institution'
            ? institution.trim()
            : '',
      });

      // Don't navigate to dashboard yet - wait for email verification
      // navigate('/dashboard');
    } catch (error) {
      console.error('Error during registration:', error.message);
      alert(error.message);
    }
  };

  const handleGoogleRegister = async () => {
    // Check if terms are accepted
    if (!termsAccepted) {
      alert('You must accept the Terms and Conditions to register.');
      return;
    }
    
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Google accounts are typically already verified
      // But we'll still update our database
      await createUserDocumentIfNotExists(user, {
        emailVerified: user.emailVerified,
        termsAccepted: true,
        termsAcceptedDate: new Date(),
      });

      // Navigate to complete info since Google provides basic info
      navigate('/complete-info');
    } catch (error) {
      console.error('Error during Google registration:', error.message);
      alert(error.message);
    }
  };

  return (
    <div className="register-container">
      <h2>Registration</h2>
      {verificationSent ? (
        <div className="verification-message">
          <h3>Email Verification Required</h3>
          <p>We've sent a verification email to <strong>{email}</strong>.</p>
          <p>Please check your inbox and click the verification link to activate your account.</p>
          <p>After verification, you can <a href="/login">login here</a>.</p>
        </div>
      ) : (
        <form onSubmit={handleRegister}>
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {passwordError && <div className="error-message">{passwordError}</div>}
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
          >
            <option value="">Select your role</option>
            <option value="student">Student</option>
            <option value="staff">Staff</option>
            <option value="institution">Institution</option>
          </select>
          {(role === 'student' || role === 'institution') && (
            <input
              type="text"
              placeholder="Institution/University/School"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              required
            />
          )}
          <div className="terms-checkbox">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <label htmlFor="terms">
              I accept the <Link to="/privacy" target="_blank">Terms and Conditions</Link>
            </label>
          </div>
          <button type="submit">Register</button>
        </form>
      )}
      <div className="google-login">
        <button onClick={handleGoogleRegister} className="google-button">
          <FontAwesomeIcon icon={faGoogle} /> Register with Google
        </button>
      </div>
    </div>
  );
};

export default Register;
